/**
 * Pixel de Meta — carga ACOTADA a las superficies del embudo comercial.
 *
 * POR QUÉ NO VA EN `public/index.html`, que es el idioma estándar y cuesta la mitad:
 * `app.pux.app` es la herramienta de trabajo de los clientes — su inbox, sus conversaciones,
 * sus bots. Meterlo ahí significa mandar a Meta la IP, el navegador y los clics de gente que
 * YA PAGA y está gestionando su negocio. No aporta nada publicitariamente —esas personas ya
 * convirtieron— y es incoherente con un producto que vende control de datos.
 *
 * LAS SUPERFICIES, y el evento que cada una dispara (todas con su función aquí):
 *
 *   superficie                          evento                 mitad de servidor
 *   ──────────────────────────────────  ─────────────────────  ─────────────────────────────
 *   ConsentGate · primera aceptación    CompleteRegistration   trigger `metaRegistroCapi`
 *   /contratar · montaje                ViewContent            — (sólo navegador)
 *   /contratar · sesión de Stripe creada InitiateCheckout      `createCheckoutSessionHttp`
 *   /dashboard?session_id · confirmado  Purchase               `stripeWebhook`
 *
 * `PageView` NO se dispara en la app: se dispara en `pux.app` (landing) y ahí mide visitas.
 * Aquí un PageView mezclaría prospectos con clientes que ya pagan bajo el mismo nombre.
 *
 * DEDUPLICACIÓN. Cada evento con mitad de servidor lleva un `eventID` que EXISTE en los dos
 * lados sin transportarlo: el id de sesión de Stripe (InitiateCheckout, Purchase) o el uid
 * (CompleteRegistration). Nunca se genera un id en el cliente para un evento que el servidor
 * también manda: dos ids distintos son dos eventos para Meta.
 *
 * AISLAMIENTO DE PRODUCCIÓN. `cargarPixel` sólo inyecta `fbevents.js` en `HOSTS_AUTORIZADOS`;
 * en cualquier otro host `window.fbq` nunca existe y todas las funciones de aquí son no-op.
 * Es UNA puerta, en UN sitio: ningún llamante comprueba el host por su cuenta.
 *
 * ⚠️ CONDICIÓN C-01. El `fbevents.js` de Meta contiene código que lee `input.value` y normaliza
 * correos y nombres; sólo excluye `password`. Está inerte porque las COINCIDENCIAS AVANZADAS
 * AUTOMÁTICAS están desactivadas en el portal — un interruptor del SERVIDOR de Meta, no del
 * código. Medido el 2026-08-21 con un banco local: `formFeatures` mandó sólo estructura
 * (`id`, `tag`, `placeholder`, `inputType`), ningún `value`, con control positivo.
 *
 * **Si ese interruptor se enciende, el correo del formulario de `/contratar` empieza a viajar
 * sin que nada aquí cambie.** Por eso `init` va SIN objeto de datos de usuario: no se reactiva
 * por código lo que está apagado en el portal.
 */

const PIXEL_ID = '1596138978912793';

/**
 * Los únicos hosts desde los que el pixel se inicializa. Exacto, no por sufijo: un
 * `preview--pux.app` o un `pux.app.evil.test` no cuentan. `localhost`, `127.0.0.1`, los
 * previews de Firebase (`*.web.app`) y una IP directa quedan fuera por no estar en la lista,
 * no por estar en una lista negra que habría que mantener.
 *
 * La landing lleva su propia copia de los dos primeros en `landing/meta-pixel.js` (no puede
 * importar de `src/`); el test `landingMetaPixel` comprueba que coinciden.
 */
const HOSTS_AUTORIZADOS = Object.freeze(['pux.app', 'www.pux.app', 'app.pux.app']);

export function esHostAutorizado(hostname) {
  return typeof hostname === 'string' && HOSTS_AUTORIZADOS.includes(hostname.toLowerCase());
}

function hostActual() {
  try { return window.location.hostname; } catch (err) { return ''; }
}

/**
 * Inserta `fbevents.js` una sola vez por sesión de página e inicializa el pixel.
 * Devuelve `true` si el pixel queda (o ya estaba) inicializado; `false` fuera de producción.
 *
 * `fbq` ENCOLA: el fragmento oficial define `n.queue=[]` y empuja las llamadas ahí hasta que el
 * script asíncrono carga. Por eso `evento()` justo después de esto es seguro y no hay carrera.
 */
export function cargarPixel() {
  if (typeof window === 'undefined') return false;
  if (!esHostAutorizado(hostActual())) return false;   // LA puerta dev/prod
  if (window.fbq) return true;                          // ya cargado: no se duplica el init

  /* eslint-disable */
  !function(f,b,e,v,n,t,s){
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  }(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', PIXEL_ID);                 // SIN datos de usuario — ver C-01 arriba
  return true;
}

/**
 * Dispara un evento estándar. NUNCA lanza.
 *
 * Doble red: un bloqueador impide cargar `fbevents.js` y `fbq` no existe; y fuera de
 * producción `cargarPixel` nunca lo definió. Ésta es la pantalla por donde entra el dinero —
 * el pixel no puede romperla bajo ninguna circunstancia.
 */
export function evento(nombre, params, opciones) {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      // El CUARTO argumento de `fbq('track')` es el objeto de opciones, y es donde va `eventID`
      // — el único campo que Meta documenta ahí. Se omite si no se pasa para no mandar `undefined`.
      if (opciones) window.fbq('track', nombre, params || {}, opciones);
      else window.fbq('track', nombre, params);
    }
  } catch (err) { /* el pixel nunca bloquea el checkout */ }
}

/**
 * LA FÓRMULA DEL IMPORTE — copia letra por letra de `importeDeSesion` en
 * `functions/integrations/utils/metaCapi.js`. Es lo que garantiza que el navegador y la CAPI
 * manden EL MISMO `value` y la MISMA `currency` para la misma sesión de Stripe: los dos parten
 * de `amount_total` (céntimos) y `currency` (minúsculas) tal como los da Stripe.
 *
 * Un importe de 0 (cupón del 100 %) ES un importe válido y se manda como 0.
 * El test `metaPixelPurchase` carga los dos ficheros y comprueba que dan lo mismo.
 */
export function importeDeSesion(amountTotalCents, currency) {
  const value = (typeof amountTotalCents === 'number' && Number.isFinite(amountTotalCents))
    ? amountTotalCents / 100
    : null;
  const cur = (typeof currency === 'string' && currency.trim())
    ? currency.trim().toUpperCase()
    : null;
  return { value, currency: cur };
}

/** Sólo entra en el evento lo que tiene valor: nunca `value: null`. */
function paramsDeImporte(amountTotalCents, currency) {
  const i = importeDeSesion(amountTotalCents, currency);
  const p = {};
  if (i.value !== null) p.value = i.value;
  if (i.currency !== null) p.currency = i.currency;
  return p;
}

/**
 * LA FÓRMULA DEL DESCUENTO — copia letra por letra de `descuentoDeSesion` en
 * `src/utils/googleTag.js` y equivalente a la de `functions/integrations/utils/metaCapi.js`.
 * Misma razón que el importe: Meta se queda con la mitad que llega PRIMERO y no fusiona, así
 * que si las dos mandaran `coupon` distinto, el cupón reportado sería el de la carrera.
 *
 * NO cambia la semántica de `value`, que sigue siendo el importe COBRADO (0 con un cupón del
 * 100 %). `discount` y `coupon` existen para que ese 0 sea explicable y no parezca una rotura.
 *
 * `discount: null` cuando no hubo descuento —y también cuando fue de 0 céntimos—: "no hubo
 * descuento" no es "descuento de 0", y el parámetro se omite en vez de mandar ruido.
 * Los dos salen de `checkoutConfirmado`, que es la ÚNICA fuente: el navegador no dispara sin
 * esa confirmación y la CAPI la lee del mismo documento, así que no pueden divergir.
 */
export function descuentoDeSesion(amountDiscountCents, promoCode) {
  const discount = (typeof amountDiscountCents === 'number' && Number.isFinite(amountDiscountCents) && amountDiscountCents > 0)
    ? amountDiscountCents / 100
    : null;
  const cupon = (typeof promoCode === 'string' && promoCode.trim())
    ? promoCode.trim().slice(0, 100)
    : null;
  return { discount, coupon: cupon };
}

/** Sólo entra en el evento lo que tiene valor: nunca `discount: null` ni `coupon: null`. */
function paramsDeDescuento(amountDiscountCents, promoCode) {
  const d = descuentoDeSesion(amountDiscountCents, promoCode);
  const p = {};
  if (d.discount !== null) p.discount = d.discount;
  if (d.coupon !== null) p.coupon = d.coupon;
  return p;
}

/**
 * `ViewContent` · la persona VIO la pantalla comercial de planes. Sólo navegador: el servidor
 * no tiene forma fiable de saber que se montó una pantalla. NO es InitiateCheckout — abrir
 * `/contratar` no es empezar a pagar.
 */
export function viewContentPlanes() {
  if (!cargarPixel()) return false;
  evento('ViewContent', { content_name: 'planes', content_category: 'contratacion' });
  return true;
}

/**
 * `InitiateCheckout` · la sesión de Stripe EXISTE. Se llama con lo que devolvió
 * `createCheckoutSessionHttp` —id de sesión e importe de la sesión—, que es exactamente lo que
 * el servidor acaba de mandar por CAPI con ese mismo `event_id`.
 */
export function initiateCheckout({ sessionId, amountTotal, currency, plan }) {
  if (!sessionId) return false;
  if (!cargarPixel()) return false;
  const params = Object.assign(
    { content_name: plan || null, content_category: 'plan' },
    paramsDeImporte(amountTotal, currency)
  );
  if (params.content_name === null) delete params.content_name;
  evento('InitiateCheckout', params, { eventID: sessionId });
  return true;
}

/**
 * `Purchase` del navegador, la mitad que Meta deduplica contra la CAPI.
 *
 * SÓLO SE DISPARA CON LA CONFIRMACIÓN DEL SERVIDOR. `Dashboard.js` escucha `clientes/{uid}` y
 * llama aquí cuando el webhook escribió `checkoutConfirmado` con el MISMO `sessionId` que trae
 * la URL de retorno. El `?session_id=` de la URL lo pone Stripe, pero cualquiera puede
 * teclearlo: no es prueba de nada. La confirmación sí, y además trae `amount_total` y
 * `currency` reales — de ahí sale el importe, con la misma fórmula que el servidor.
 *
 * EL `event_id` ES EL `session_id` DE STRIPE. Meta sólo exige que `event_id` y `event_name`
 * coincidan entre las dos vías, dentro de 48 h; el id de sesión cumple sin transportar nada.
 *
 * GUARDA EN `sessionStorage`, NO EN `useRef`. El efecto que detecta el `session_id` puede
 * remontarse; un `useRef` no sobrevive al remontaje, y sin guarda cada compra se contaría DOS
 * VECES desde el navegador — y eso la deduplicación NO lo salva: Meta deduplica navegador contra
 * servidor, no navegador contra navegador.
 *
 * Safari en modo privado LANZA al tocar `sessionStorage`: si no se puede leer la marca, se
 * prefiere NO disparar. Perder un evento es barato (la CAPI lo cubre); duplicar una compra
 * falsea ingresos.
 */
export function purchaseUnaVez(sessionId, confirmacion) {
  if (!sessionId) return false;
  if (!confirmacion || confirmacion.sessionId !== sessionId) return false;   // sin confirmación, nada
  const marca = 'pux_purchase_sent:' + sessionId;
  try {
    if (window.sessionStorage.getItem(marca)) return false;
    window.sessionStorage.setItem(marca, '1');
  } catch (err) {
    return false;   // sin almacenamiento no hay guarda: no se dispara
  }
  if (!cargarPixel()) return false;
  evento('Purchase', {
    ...paramsDeImporte(confirmacion.amountTotal, confirmacion.currency),
    ...paramsDeDescuento(confirmacion.amountDiscount, confirmacion.promoCode),
  }, { eventID: sessionId });
  return true;
}

/**
 * `CompleteRegistration` · en qué contexto se aceptaron los términos.
 *
 *   'alta'        → embudo comercial: se dispara (navegador y CAPI).
 *   'invitacion'  → invitado a una organización ajena: no va a comprar; no se dispara.
 *   'desarrollo'  → host no autorizado: no se dispara, y el servidor tampoco.
 *
 * Es una función pura sobre sus entradas para poder probarla; `OrganizationContext` le pasa
 * lo que sabe. El servidor NO puede deducir esto solo —el token de invitación vive en
 * `localStorage`— y por eso el valor se escribe en `registroMeta.contexto` junto con la
 * aceptación, y `metaRegistroCapi` lo respeta.
 */
export function contextoDeRegistro({ hostname, pathname, tienePendingInvite }) {
  if (!esHostAutorizado(hostname)) return 'desarrollo';
  if (tienePendingInvite) return 'invitacion';
  if (typeof pathname === 'string' && pathname.startsWith('/accept-invitation/')) return 'invitacion';
  return 'alta';
}

/**
 * Dispara `CompleteRegistration` UNA vez por uid.
 * `eventID` = uid: el trigger de servidor usa el mismo, y Meta deduplica las dos mitades.
 */
export function completeRegistration({ uid, contexto }) {
  if (!uid || contexto !== 'alta') return false;
  const marca = 'pux_registration_sent:' + uid;
  try {
    if (window.sessionStorage.getItem(marca)) return false;
    window.sessionStorage.setItem(marca, '1');
  } catch (err) {
    return false;
  }
  if (!cargarPixel()) return false;
  evento('CompleteRegistration', { content_name: 'alta', status: true }, { eventID: uid });
  return true;
}

/**
 * Lee la cookie `_fbp` que escribe el pixel.
 *
 * ÁMBITO, medido leyendo `fbevents.js` y no supuesto: el pixel la fija con `domain=.<dominio>`
 * —con punto inicial— sondeando de más ancho a más estrecho y quedándose en el primer nivel que
 * el navegador acepta. Para `pux.app` eso es `.pux.app`, porque `.app` es sufijo público y se
 * rechaza. Por eso una `_fbp` escrita en la landing es legible desde `app.pux.app`.
 *
 * Devuelve `null` si no existe: quien no pasó por la landing no la tiene.
 */
export function leerFbp() {
  return leerCookie('_fbp');
}

/**
 * Lee `_fbc` (el identificador de CLIC, que sólo existe si la visita trajo `fbclid`), o lo
 * construye si la cookie no está pero sí se capturó el `fbclid` en la URL.
 *
 * Es la señal más fuerte para atribuir una conversión a un anuncio concreto, y se estaba
 * perdiendo entera: el pixel la escribía y nadie la leía.
 *
 * FORMATO, el que Meta documenta para construirlo uno mismo: `fb.1.<ms de captura>.<fbclid>`.
 * El `1` es el índice de subdominio para `pux.app` (cookie en `.pux.app`). NO se inventa: sin
 * cookie y sin `fbclid` capturado, devuelve `null`. Un `fbclid` con caracteres fuera de
 * `[A-Za-z0-9_-]` se descarta — no es de Meta.
 *
 * @param {{fbclid?: string, ts?: number}|null} respaldo  lo que capturó `attribution.js`
 */
export function leerFbc(respaldo) {
  const cookie = leerCookie('_fbc');
  if (cookie && /^fb\.\d\.\d+\..+$/.test(cookie)) return cookie;
  const fbclid = respaldo && typeof respaldo.fbclid === 'string' ? respaldo.fbclid : '';
  const ts = respaldo && typeof respaldo.ts === 'number' && Number.isFinite(respaldo.ts) ? Math.floor(respaldo.ts) : null;
  if (!fbclid || !ts || !/^[A-Za-z0-9_-]{1,512}$/.test(fbclid)) return null;
  return 'fb.1.' + ts + '.' + fbclid;
}

function leerCookie(nombre) {
  try {
    const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + nombre + '=([^;]+)'));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (err) {
    return null;
  }
}

export { PIXEL_ID, HOSTS_AUTORIZADOS };
